import IInputMaskItem, { MaskValue } from './IInputMaskItem';

export default class InputMaskNumberRange extends IInputMaskItem {
    mask: string;
    min: number;
    max: number;

    constructor(mask: string, min: number, max: number) {
        super();
        if (mask.length != max.toString(10).length) {
            throw new Error(`Mask length must match the length of the maximum number. Mask: ${mask}, max: ${max}`);
        }

        if (min > max) {
            throw new Error(`Min cannot be larger than min. Got min: ${min}, max: ${max}`);
        }

        this.mask = mask;
        this.min = min;
        this.max = max;

        this.maskValue = Array.from(max.toString(10), (_, i) => new MaskValue(mask[i]));
    }

    addUserInput(char: string, charIndex: number): boolean {
        if (char.length != 1) {
            return false;
        }

        const intParsedChar = parseInt(char);
        if (isNaN(intParsedChar)) {
            return false;
        }

        // Clone maskValue so we can temporarily alter it
        const maskValue = this.maskValue.map((mv) => new MaskValue(mv.placeHolder, mv.userValue));

        maskValue[charIndex].userValue = char;

        let currentValue = this.getTotalMaskValue(maskValue);

        if (currentValue > this.max) {
            // In this case we should unset the less significant digits and check again
            maskValue.forEach((mv, i) => {
                mv.userValue = i > charIndex ? null : mv.userValue;
            });

            currentValue = this.getTotalMaskValue(maskValue);
            if (currentValue > this.max) {
                return false;
            }
        }

        // Only check for min of the least significant digit has been filled.
        // If you want the first of a month you will put 01, the first value we will check is 0d, which calculates to 0 and is less than 1
        if (this.isLeastSignificantFilled(maskValue)) {
            if (currentValue < this.min) {
                return false;
            }
        }

        // Our temp change was accepted so make it permanent
        this.maskValue = maskValue;

        return true;
    }

    removeUserInput(charIndex: number): boolean {
        if (charIndex < 0 || charIndex > this.maskValue.length) {
            throw new Error(`Mask index cannot be out of bounds. Index: ${charIndex}, maskLength: ${this.maskValue.length}`);
        }

        if (this.maskValue[charIndex].userValue == null) {
            return false;
        }

        this.maskValue[charIndex].userValue = null;

        return true;
    }

    /**
     * Return the uservalue if filled, or else the placeholder value
     * @returns
     */
    getCurrentDisplayValue(): string {
        return this.maskValue.reduce((res, mv) => res + (mv.userValue || mv.placeHolder), '');
    }

    /**
     * This will return empty string where the userValue is not filled
     * @returns
     */
    getCurrentTextForInputElement(): string {
        return this.maskValue.reduce((res, mv) => res + (mv.userValue || ' '), '');
    }

    /**
     * Check if the user value is filled.
     * @returns
     */
    isMaskFilled(): boolean {
        return this.maskValue.every((mi) => mi.userValue);
    }

    private isLeastSignificantFilled(maskValue: MaskValue[]) {
        return maskValue[maskValue.length - 1].userValue != null;
    }

    private getMaskValue(index: number, maskValue: MaskValue[]) {
        if (index < 0 || index > this.maskValue.length) {
            throw new Error(`Mask index cannot be out of bounds. Index: ${index}, maskLength: ${this.maskValue.length}`);
        }

        const mv = maskValue[index];
        if (mv.userValue == null) {
            return 0;
        }

        const placeFactor = Math.pow(10, this.mask.length - index - 1);

        return parseInt(mv.userValue) * placeFactor;
    }

    private getTotalMaskValue(maskValue: MaskValue[]) {
        let total = 0;
        for (let i = 0; i < this.maskValue.length; i++) {
            total += this.getMaskValue(i, maskValue);
        }
        return total;
    }
}
